import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import styles from './ReviewModal.module.css';
import { authenticatedFetch } from '../../utils/api';

const ReviewModal = ({ coachId, bookingId, onClose }) => {
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [review, setReview] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const fetchExistingReview = async () => {
            try {
                const data = await authenticatedFetch(`/api/review/coach/${bookingId}`);
                if (data.review) {
                    setRating(data.review.rating);
                    setReview(data.review.review);
                }
            } catch (err) {
                console.error('Error fetching existing review:', err);
            }
        };

        fetchExistingReview();
    }, [bookingId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating === 0) {
            setError('Please select a rating');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await authenticatedFetch('/api/review/coach', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    coachId,
                    bookingId,
                    rating,
                    review
                })
            });

            setSubmitted(true);
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (err) {
            setError(err.message || 'Failed to submit review. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                    <div className={styles.successMessage}>
                        <h2>Review Submitted</h2>
                        <p>Thank you for your feedback. Your review has been recorded successfully.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>Session Review</h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.reviewForm}>
                    <div className={styles.ratingSection}>
                        <label className={styles.ratingLabel}>
                            Rate your experience
                        </label>
                        <div className={styles.starsContainer}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={styles.starButton}
                                    onClick={() => setRating(star)}
                                    onMouseEnter={() => setHoveredRating(star)}
                                    onMouseLeave={() => setHoveredRating(0)}
                                >
                                    <Star
                                        className={styles.star}
                                        fill={star <= (hoveredRating || rating) ? '#FFD700' : 'none'}
                                        color={star <= (hoveredRating || rating) ? '#FFD700' : '#FFD700'}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.feedbackSection}>
                        <label htmlFor="review" className={styles.textareaLabel}>
                            Share your feedback
                        </label>
                        <textarea
                            id="review"
                            className={styles.reviewInput}
                            placeholder="What aspects of the coaching session were most valuable to you?"
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                            maxLength={500}
                        />
                    </div>

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <div className={styles.actionButtons}>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.primaryButton}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReviewModal;